import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Divider, IconButton } from '@mui/material';
import { Pinterest, Email } from '@mui/icons-material';
import { PALETTE, BORDERS, BORDER_COLORS, TINTS } from '../theme';

const linkSx = {
    color: PALETTE.textBody,
    textDecoration: 'none',
    fontSize: '0.92rem',
    fontWeight: 500,
    transition: 'color 0.15s ease',
    '&:hover': { color: PALETTE.text }
};

// Bluesky butterfly — MUI does not ship a Bluesky icon, so inline.
const BlueskyGlyph = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
        <path d="M6.34 4.8C9.14 6.85 12 11.05 12 13.18c0-2.13 2.86-6.33 5.66-8.38 2.02-1.49 5.34-2.64 5.34 1 0 .73-.42 6.12-.66 7-.86 3.05-3.96 3.83-6.7 3.37 4.81.8 6.04 3.5 3.4 6.2-5 5.15-7.18-1.29-7.74-2.95l-.05-.13c-.07.16-.1.1 0 0-.55 1.66-2.74 8.1-7.74 2.95-2.64-2.7-1.42-5.4 3.4-6.2-2.74.46-5.84-.32-6.7-3.37-.24-.88-.66-6.27-.66-7 0-3.64 3.32-2.49 5.34-1z" />
    </svg>
);

const SocialButton = ({ href, label, children }) => (
    <IconButton
        component="a"
        href={href}
        aria-label={label}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
            color: PALETTE.textMuted,
            border: BORDERS.soft,
            borderRadius: 1.5,
            width: 36,
            height: 36,
            transition: 'all 0.15s ease',
            '&:hover': {
                color: PALETTE.primaryDark,
                borderColor: BORDER_COLORS.primary,
                backgroundColor: TINTS.primaryGlow
            }
        }}
    >
        {children}
    </IconButton>
);

const Footer = () => {
    const year = new Date().getFullYear();

    return (
        <Box
            component="footer"
            sx={{
                borderTop: BORDERS.soft,
                backgroundColor: PALETTE.surface,
                mt: { xs: 6, sm: 8 },
                py: { xs: 4, sm: 5 },
                px: { xs: 2, sm: 3 }
            }}
        >
            <Box
                sx={{
                    maxWidth: 1200,
                    mx: 'auto',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    justifyContent: 'space-between',
                    gap: { xs: 3, md: 2 }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box
                        component="img"
                        src="/brand/logo.png"
                        alt="Map Shaper"
                        sx={{ width: 32, height: 32, borderRadius: 1.25, display: 'block' }}
                    />
                    <Box>
                        <Typography sx={{ fontWeight: 700, color: PALETTE.text, lineHeight: 1.1 }}>
                            Map Shaper
                        </Typography>
                        <Typography variant="caption" sx={{ color: PALETTE.textMuted }}>
                            Travel maps, trail maps, road trips & map-shaped photos
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2.25, sm: 3 }, alignItems: 'center' }}>
                    <Box component={RouterLink} to="/about"    sx={linkSx}>About</Box>
                    <Box component={RouterLink} to="/privacy"  sx={linkSx}>Privacy</Box>
                    <Box component={RouterLink} to="/shipping" sx={linkSx}>Shipping</Box>
                    <Box component="a" href="mailto:mapshaped@gmail.com" sx={linkSx}>Contact</Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <SocialButton href="https://www.pinterest.com/mapshaped/" label="Pinterest">
                        <Pinterest sx={{ fontSize: 20 }} />
                    </SocialButton>
                    <SocialButton href="https://bsky.app/profile/map-shaped.bsky.social" label="Bluesky">
                        <BlueskyGlyph />
                    </SocialButton>
                    <SocialButton href="mailto:mapshaped@gmail.com" label="Email">
                        <Email sx={{ fontSize: 20 }} />
                    </SocialButton>
                </Box>
            </Box>

            <Divider sx={{ my: { xs: 3, sm: 3 }, maxWidth: 1200, mx: 'auto' }} />

            <Box
                sx={{
                    maxWidth: 1200,
                    mx: 'auto',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    gap: 1
                }}
            >
                <Typography variant="caption" sx={{ color: PALETTE.textFaint }}>
                    © {year} Map Shaper. All rights reserved.
                </Typography>
                <Typography variant="caption" sx={{ color: PALETTE.textFaint }}>
                    Products printed and shipped by Zazzle.
                </Typography>
            </Box>
        </Box>
    );
};

export default Footer;
